import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { vocabOf } from '../../utils/vertical';
import { startOnlinePayment } from '../../services/payment';
import PageHeader from '../../components/PageHeader';
import {
  Button, Chip, Empty, Input, Loading, Row,
} from '../../components/Ui';
import ChargeRows from '../../components/ChargeRows';
import { applyCharges, loadCharges } from '../../utils/charges';
import { Reveal, Pop } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { currency, nextDays } from '../../utils/format';
import { TIME_SLOTS } from '../../constants';
import AddressPicker from './AddressPicker';

/** Slot picker + address + payment mode, then creates the booking. */
const BookServicePage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const toast = useToast();

  const days = nextDays(14);

  const [service, setService] = useState((state && state.service) || null);
  const [loading, setLoading] = useState(!(state && state.service));
  const [addressId, setAddressId] = useState(null);
  const [date, setDate] = useState(days[0].iso);
  const [slot, setSlot] = useState(TIME_SLOTS[1]);
  const [quantity, setQuantity] = useState(1);
  // Admin's taxes & extras on top of the provider's price.
  const [chargeRules, setChargeRules] = useState({});
  useEffect(() => { loadCharges().then(setChargeRules); }, []);
  // Vehicle servicing asks whether the garage should collect the vehicle.
  const askVehicle = Boolean(service) && (
    vocabOf(service.partner).key === 'garage'
    || (/vehicle|car|bike|scooter|servic/i.test(`${service.title} ${(service.category && service.category.name) || ''}`) && vocabOf(service.partner).key !== 'service')
  );
  const [requirement, setRequirement] = useState('');
  const [pickupDrop, setPickupDrop] = useState(false);
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [paymentMode, setPaymentMode] = useState('cod');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (service) return;
    api.services.detail(serviceId)
      .then((res) => setService(res.data))
      .catch(() => setService(null))
      .finally(() => setLoading(false));
  }, [serviceId, service]);

  const onAddresses = useCallback((list) => {
    const preferred = list.find((a) => a.isDefault) || list[0];
    if (preferred) setAddressId((cur) => cur || preferred._id);
  }, []);

  if (loading) return <Loading text="Loading service…" />;
  if (!service) {
    return <Empty emoji="🛠️" title="Service not found" actionLabel="Go back" onAction={() => navigate(-1)} />;
  }

  const base = (service.price || 0) * quantity;
  const bill = applyCharges(chargeRules.booking, base);
  const total = bill.total;

  const submit = async () => {
    if (!addressId) {
      toast.error('Add a service address to continue.', 'Address needed');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.bookings.create({
        serviceId: service._id,
        partnerId: service.partner && service.partner._id,
        addressId,
        scheduledDate: date,
        scheduledSlot: slot,
        quantity,
        requirement,
        pickupDrop: askVehicle ? pickupDrop : false,
        vehicleInfo: askVehicle ? vehicleInfo : '',
        paymentMode,
      });
      // Pay first - the provider hears of an online booking only once it is paid.
      if (paymentMode === 'online') {
        try {
          await startOnlinePayment({ refType: 'booking', refId: res.data._id });
          setDone({ ...res.data, paymentStatus: 'paid', awaitingPayment: false });
          window.scrollTo({ top: 0 });
        } catch (err) {
          toast.error('Your booking is sent to the professional only after payment. Retry, or choose to pay after the service.',
            err.cancelled ? 'Payment cancelled' : 'Payment not completed');
          navigate(`/bookings/${res.data._id}`);
        }
        return;
      }
      setDone(res.data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(err.message, 'Could not book');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="center-page">
        <div style={{ maxWidth: 440, width: '100%' }}>
          <Pop>
            <div
              style={{
                width: 104, height: 104, borderRadius: 52, background: colors.successBg,
                display: 'grid', placeItems: 'center', fontSize: 46, margin: '0 auto',
              }}
            >
              ✅
            </div>
          </Pop>
          <h1 className="h1" style={{ marginTop: 20 }}>Booking requested</h1>
          <p className="t-body" style={{ color: colors.muted, marginTop: 8, lineHeight: 1.6 }}>
            {service.partner && service.partner.businessName} will confirm your{' '}
            {service.title} booking shortly.
          </p>

          <div className="card sh-sm" style={{ marginTop: 20, textAlign: 'left' }}>
            <Row label="Booking number" value={done.bookingNo} />
            <Row label="Scheduled" value={`${date} · ${slot}`} />
            <Row label="Amount" value={currency(done.totalAmount)} />
            <Row label="Payment" value={done.paymentStatus === 'paid' ? 'Paid online ✓' : 'Pay after service'} />
            <Row
              label="Start OTP"
              value={done.otp}
              valueStyle={{ color: colors.primary, fontWeight: 800, fontSize: 17 }}
            />
          </div>
          <p className="t-tiny" style={{ marginTop: 12 }}>
            Share the start OTP with the professional when they arrive.
          </p>

          <div className="col" style={{ gap: 12, marginTop: 24 }}>
            <Button title="View my bookings" size="lg" full onClick={() => navigate('/activity')} />
            <Button title="Back to home" variant="light" full onClick={() => navigate('/home')} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="has-action-bar">
      <PageHeader title="Book service" subtitle={service.title} />

      <div className="split">
        <div>
          {/* Date */}
          <Reveal className="card sh-sm">
            <label className="field-label">Select a date</label>
            <div className="rail no-bar">
              {days.map((d) => {
                const active = date === d.iso;
                return (
                  <button
                    key={d.iso}
                    type="button"
                    onClick={() => setDate(d.iso)}
                    className="press"
                    style={{
                      width: 66, padding: '12px 0', borderRadius: 12, textAlign: 'center',
                      border: `1px solid ${active ? colors.primary : colors.line}`,
                      background: active ? colors.primary : '#fff',
                      color: active ? '#fff' : colors.ink,
                      transition: 'background .16s ease, border-color .16s ease',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, opacity: active ? 0.85 : 0.6 }}>
                      {d.isToday ? 'Today' : d.day}
                    </div>
                    <div style={{ fontSize: 19, fontWeight: 800 }}>{d.num}</div>
                    <div style={{ fontSize: 10.5, opacity: active ? 0.85 : 0.6 }}>{d.month}</div>
                  </button>
                );
              })}
            </div>

            <label className="field-label" style={{ marginTop: 20 }}>Preferred time slot</label>
            <div className="row wrap" style={{ gap: 8 }}>
              {TIME_SLOTS.map((s) => (
                <Chip key={s} label={s} active={slot === s} onClick={() => setSlot(s)} />
              ))}
            </div>

            <label className="field-label" style={{ marginTop: 20 }}>Quantity</label>
            <div className="row" style={{ gap: 12 }}>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="press"
                style={{
                  width: 38, height: 38, borderRadius: 12, background: '#fff',
                  border: `1px solid ${colors.line}`, fontSize: 18, fontWeight: 700,
                }}
              >
                −
              </button>
              <span style={{ fontSize: 18, fontWeight: 800, minWidth: 24, textAlign: 'center' }}>{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => q + 1)}
                className="press"
                style={{
                  width: 38, height: 38, borderRadius: 12, background: '#fff',
                  border: `1px solid ${colors.line}`, fontSize: 18, fontWeight: 700,
                }}
              >
                +
              </button>
              <span className="t-small">× {currency(service.price)}</span>
            </div>
          </Reveal>

          {/* Address */}
          <Reveal delay={80} style={{ marginTop: 16 }}>
            <AddressPicker
              title="Service address"
              value={addressId}
              onChange={setAddressId}
              onLoaded={onAddresses}
            />
          </Reveal>

          {askVehicle ? (
            <Reveal delay={130} className="card sh-sm" style={{ marginTop: 16 }}>
              <label className="field-label">Do you need pickup & drop?</label>
              <div className="row wrap" style={{ gap: 8, marginBottom: 8 }}>
                <Chip label="🚚 Yes, collect my vehicle" active={pickupDrop} onClick={() => setPickupDrop(true)} />
                <Chip label="🏪 No, I will come in" active={!pickupDrop} onClick={() => setPickupDrop(false)} />
              </div>
              <div className="t-tiny" style={{ marginBottom: 10 }}>
                {pickupDrop
                  ? 'They pick the vehicle up from your address at the time chosen above and bring it back after the service.'
                  : 'The date and time above are your appointment.'}
              </div>
              <Input label="Your vehicle" placeholder="Hero Splendor · HR02AB1234" value={vehicleInfo} onChange={(e) => setVehicleInfo(e.target.value)} style={{ marginBottom: 0 }} />
            </Reveal>
          ) : null}

          {/* Requirement */}
          <Reveal delay={140} className="card sh-sm" style={{ marginTop: 16 }}>
            <Input
              label="Describe your requirement (optional)"
              textarea
              rows={3}
              placeholder="e.g. Split AC in bedroom is not cooling"
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              style={{ marginBottom: 0 }}
            />
          </Reveal>
        </div>

        {/* Summary */}
        <div className="split-side">
          <div className="action-bar">
            <div className="action-extra">
              <label className="field-label">Payment method</label>
              <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
                <Chip label="💵 Pay after service" active={paymentMode === 'cod'} onClick={() => setPaymentMode('cod')} />
                <Chip label="💳 Pay online" active={paymentMode === 'online'} onClick={() => setPaymentMode('online')} />
              </div>

              <Row label={`${service.title} × ${quantity}`} value={currency(base)} />
              <Row label="Visit charge" value="Free" />
              <ChargeRows doc={bill} />
              <div className="divider" />
            </div>

            <div className="action-main">
              <div className="t-tiny">TOTAL</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{currency(total)}</div>
            </div>

            <div className="action-cta">
              <Button
                title="Confirm booking"
                onClick={submit}
                loading={submitting}
                full
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookServicePage;
